import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const KidneyDiseaseFH = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-[#0d9488]/10 text-[#0d9488] text-sm font-medium rounded-full mb-6">
              Kidney Disease
            </div>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
              Familial Hypercholesterolemia (FH) and Kidney Disease: An Overlooked Connection
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed font-light max-w-3xl mx-auto">
              When people think about Familial Hypercholesterolemia (FH), they usually focus on heart attacks and coronary artery disease.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              But high LDL cholesterol does not only affect the heart.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              The kidneys — highly vascular organs responsible for filtering blood — can also be impacted by long-standing lipid abnormalities. When FH coexists with kidney disease, cardiovascular risk increases substantially, and management becomes more complex.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed font-light">
              Raising awareness about this relationship is critical for early detection and prevention.
            </p>
          </div>
        </div>
      </section>

      {/* Understanding the Basics */}
      <section className="py-16 bg-white/50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-12 text-center">
            Understanding the <span className="font-semibold text-[#f04e58]">Basics</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What is FH */}
            <div className="bg-gradient-to-br from-[#f04e58]/5 to-[#f04e58]/10 rounded-2xl p-8 border-2 border-[#f04e58]/20">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/20 mb-4">
                <i className="ri-dna-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">What is Familial Hypercholesterolemia?</h3>
              <p className="text-gray-700 leading-relaxed mb-4 font-light">
                FH is an inherited condition characterized by:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">LDL cholesterol typically above 190 mg/dL</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Impaired LDL receptor function</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Accelerated atherosclerosis</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Increased lifetime cardiovascular risk</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4 font-light">
                LDL levels are elevated from birth, creating cumulative vascular damage over decades.
              </p>
            </div>

            {/* What is CKD */}
            <div className="bg-gradient-to-br from-[#0d9488]/5 to-[#0d9488]/10 rounded-2xl p-8 border-2 border-[#0d9488]/20">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/20 mb-4">
                <i className="ri-heart-pulse-line text-2xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">What is Chronic Kidney Disease (CKD)?</h3>
              <p className="text-gray-700 leading-relaxed mb-4 font-light">
                Chronic kidney disease occurs when the kidneys gradually lose their ability to filter waste and maintain fluid and electrolyte balance.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4 font-light">
                Common causes include:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Diabetes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Hypertension</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Vascular disease</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Genetic conditions</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4 font-light">
                CKD itself significantly increases cardiovascular risk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How FH Affects the Kidneys */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#f04e58]/10 via-[#e67e22]/10 to-[#f04e58]/10 rounded-2xl p-8 mb-12 border-2 border-[#f04e58]/20">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">
              How FH Affects the Kidneys
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed font-light">
              Although FH is primarily known for coronary artery disease, the underlying mechanism — atherosclerosis — affects all blood vessels, including those supplying the kidneys.
            </p>
          </div>

          {/* Hero Image */}
          <div className="mb-16 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/70038971a2161a75f321fd6833c70107.jpg"
              alt="Kidney Disease and FH"
              className="w-full h-[600px] object-cover object-top"
            />
          </div>

          {/* Key Interactions */}
          <div className="space-y-8">
            {/* 1. Renal Artery Atherosclerosis */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#f04e58]/20 hover:border-[#f04e58] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#f04e58]">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Renal Artery Atherosclerosis</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    The kidneys depend on healthy renal arteries for blood flow.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    In FH:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">LDL accumulates in arterial walls</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Plaque forms in renal arteries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Blood flow may be reduced</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Renal artery stenosis can lead to:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Worsening hypertension</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Reduced kidney function</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Progressive CKD</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    Because FH accelerates plaque formation, renal arteries are not spared.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Microvascular Injury */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#0d9488]/20 hover:border-[#0d9488] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0d9488]">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Microvascular Injury</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    The kidneys contain millions of tiny blood vessels (glomeruli).
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    High LDL and vascular inflammation may:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Damage glomerular capillaries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Increase oxidative stress</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Contribute to protein leakage (proteinuria)</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    While FH alone does not directly cause kidney failure in most cases, vascular injury can worsen preexisting kidney disease.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Hypertension as a Bridge */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#e67e22]/20 hover:border-[#e67e22] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#e67e22]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#e67e22]">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Hypertension as a Bridge Between FH and CKD</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Hypertension frequently accompanies both FH and kidney disease.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-2 font-semibold">In FH:</p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-fill text-[#e67e22] mt-1 mr-2 flex-shrink-0"></i>
                          <span className="font-light">Arterial stiffness increases blood pressure</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-fill text-[#e67e22] mt-1 mr-2 flex-shrink-0"></i>
                          <span className="font-light">Renal artery narrowing may worsen hypertension</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-2 font-semibold">In CKD:</p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-fill text-[#e67e22] mt-1 mr-2 flex-shrink-0"></i>
                          <span className="font-light">Impaired kidney function further raises blood pressure</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed font-light">
                    This creates a cycle where high blood pressure and lipid-driven vascular damage amplify each other.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. CKD Increases Cardiovascular Risk */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#8b5cf6]/20 hover:border-[#8b5cf6] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#8b5cf6]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#8b5cf6]">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">CKD Increases Cardiovascular Risk in FH</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Chronic kidney disease independently increases risk of:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Coronary artery disease</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Heart failure</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Stroke</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Sudden cardiac death</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    When CKD and FH coexist:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Cardiovascular risk becomes markedly elevated</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Plaque progression accelerates</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#8b5cf6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Outcomes are often worse</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    This combination requires aggressive management.
                  </p>
                </div>
              </div>
            </div>

            {/* 5. Lipid Abnormalities in Kidney Disease */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#f59e0b]/20 hover:border-[#f59e0b] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f59e0b]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#f59e0b]">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Lipid Abnormalities in Kidney Disease</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    CKD itself alters lipid metabolism, often causing:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f59e0b] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Elevated triglycerides</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f59e0b] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Increased small dense LDL</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#f59e0b] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Reduced HDL</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    In someone with FH:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#f59e0b] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">LDL is already high</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-fill text-[#f59e0b] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Additional lipid abnormalities further increase atherosclerotic burden</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    This combination makes vascular disease more aggressive.
                  </p>
                </div>
              </div>
            </div>

            {/* 6. Proteinuria and Endothelial Dysfunction */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#ec4899]/20 hover:border-[#ec4899] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ec4899]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#ec4899]">6</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Proteinuria and Endothelial Dysfunction</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Protein leakage in urine (proteinuria) is both a marker and driver of vascular injury.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    In CKD:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#ec4899] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Endothelial dysfunction increases</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#ec4899] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Inflammation rises</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#ec4899] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Cardiovascular risk escalates</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    FH-related LDL exposure compounds this injury.
                  </p>
                </div>
              </div>
            </div>

            {/* 7. Statins and Kidney Protection */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#14b8a6]/20 hover:border-[#14b8a6] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#14b8a6]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#14b8a6]">7</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Statins and Kidney Protection</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Statins remain first-line therapy in FH and are generally safe in CKD (with dose adjustments in advanced stages).
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    Benefits include:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-[#14b8a6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Significant LDL reduction</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-[#14b8a6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Reduced cardiovascular events</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-[#14b8a6] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Potential slowing of vascular progression</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    For advanced CKD or dialysis patients, treatment decisions should be individualized.
                  </p>
                </div>
              </div>
            </div>

            {/* 8. Advanced FH and Renal Complications */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#dc2626]/20 hover:border-[#dc2626] transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#dc2626]/10 flex-shrink-0">
                  <span className="text-2xl font-bold text-[#dc2626]">8</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Advanced FH and Renal Complications</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 font-light">
                    In rare severe cases, particularly Homozygous Familial Hypercholesterolemia (HoFH):
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#dc2626] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Extreme LDL levels may cause widespread vascular disease</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#dc2626] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Renal artery involvement may occur earlier</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-fill text-[#dc2626] mt-1 mr-2 flex-shrink-0"></i>
                      <span className="font-light">Aggressive therapies such as LDL apheresis may be required</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-light">
                    Early diagnosis is critical to prevent long-term organ damage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Risk Factors */}
      <section className="py-16 bg-white/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#e67e22]/10 to-[#f59e0b]/10 rounded-2xl p-8 border-2 border-[#e67e22]/20">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">Shared Risk Factors</h2>
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              FH and kidney disease often intersect with other cardiovascular risk factors:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-[#e67e22] text-xl"></i>
                <span className="text-gray-700 font-light">Hypertension</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-[#e67e22] text-xl"></i>
                <span className="text-gray-700 font-light">Diabetes</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-[#e67e22] text-xl"></i>
                <span className="text-gray-700 font-light">Obesity</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-[#e67e22] text-xl"></i>
                <span className="text-gray-700 font-light">Smoking</span>
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed font-light mt-6">
              When multiple risk factors cluster, vascular aging accelerates significantly.
            </p>
          </div>
        </div>
      </section>

      {/* Screening Recommendations */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-12 text-center">
            Screening <span className="font-semibold text-[#0d9488]">Recommendations</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border-2 border-[#0d9488]/20 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                <i className="ri-user-heart-line text-2xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Individuals with FH should be monitored for:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Blood pressure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Kidney function (creatinine, eGFR)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Urine protein</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-[#f04e58]/20 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                <i className="ri-test-tube-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Patients with CKD should be evaluated for FH if:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">LDL levels exceed 190 mg/dL</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                  <span className="font-light">Family history of early heart disease</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed font-light mt-4">
                Family history of early heart disease strengthens suspicion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Importance of Early Detection */}
      <section className="py-16 bg-gradient-to-br from-[#0d9488]/5 to-[#14b8a6]/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-8 text-center">
            The Importance of <span className="font-semibold text-[#0d9488]">Early Detection</span>
          </h2>

          <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg">
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              The key concept in FH is <strong>lifetime LDL burden</strong> — cumulative exposure to high LDL cholesterol.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              When kidney disease is added to the equation:
            </p>
            <ul className="space-y-3 text-gray-700 mb-6">
              <li className="flex items-start">
                <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                <span className="font-light">Vascular injury accelerates</span>
              </li>
              <li className="flex items-start">
                <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                <span className="font-light">Cardiovascular risk multiplies</span>
              </li>
              <li className="flex items-start">
                <i className="ri-alert-fill text-[#f04e58] mt-1 mr-2 flex-shrink-0"></i>
                <span className="font-light">Outcomes worsen</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              However, early intervention can dramatically alter this trajectory.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border-2 border-[#0d9488]/20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-4">
                <i className="ri-heart-pulse-line text-3xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reduce cardiovascular events</h3>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border-2 border-[#0d9488]/20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Slow vascular progression</h3>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border-2 border-[#0d9488]/20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-4">
                <i className="ri-time-line text-3xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Improve long-term survival</h3>
            </div>
          </div>

          <p className="text-center text-lg text-gray-700 leading-relaxed font-light mt-8">
            Aggressive LDL reduction combined with strict blood pressure control can transform outcomes.
          </p>
        </div>
      </section>

      {/* Raising Awareness */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-8 text-center">
            Raising <span className="font-semibold text-[#f04e58]">Awareness</span>
          </h2>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-100 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              FH affects approximately 1 in 250 individuals.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed font-light mb-6">
              Many people with both kidney disease and high cholesterol may not realize that an inherited condition is driving their risk.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              If you or a family member has:
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700 font-light">LDL above 190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700 font-light">Early heart disease</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700 font-light">Family history of premature cardiovascular events</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700 font-light">Kidney disease with unusually high cholesterol</span>
              </li>
            </ul>
            <p className="text-gray-900 font-semibold mt-6 text-lg">
              Ask about evaluation for Familial Hypercholesterolemia.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-8">Final Takeaway</h2>
            <p className="text-white/90 leading-relaxed mb-4 text-lg font-light">
              Familial Hypercholesterolemia is not just a heart condition.
            </p>
            <p className="text-white/90 leading-relaxed mb-4 text-lg font-light">
              It is a systemic vascular disorder that can affect arteries throughout the body — including those supplying the kidneys.
            </p>
            <p className="text-white/90 leading-relaxed mb-4 text-lg font-light">
              When FH and kidney disease coexist, cardiovascular risk rises sharply.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6 text-center">The Encouraging News</h3>
            <p className="text-white/90 leading-relaxed text-center text-lg mb-6 font-light">
              Early diagnosis and comprehensive management can significantly reduce complications.
            </p>
            <p className="text-white font-semibold text-center text-xl">
              Awareness of FH protects not only the heart — but the kidneys and the entire vascular system.
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Schedule Your Evaluation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KidneyDiseaseFH;
